import { Component, OnInit, input, output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { PlannerService, Task, TodoList, TodoItem } from '../../../core/services/planner.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-planner-task-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, CheckboxModule],
  templateUrl: './planner-task-detail.component.html',
  styleUrls: ['./planner-task-detail.component.scss'],
})
export class PlannerTaskDetailComponent implements OnInit {
  task = input<Task | null>(null);
  workspaceUuid = input<string>('');
  listUuid = input<string>('');
  taskUpdated = output<Task>();
  closed = output<void>();

  newTodoListTitle: string = '';
  newTodoItemTitles: Map<string, string> = new Map();
  plannerService = inject(PlannerService);
  translocoService = inject(TranslocoService);

  constructor() {}

  ngOnInit() {}

  addTodoList(): void {
    const currentTask = this.task();
    if (!currentTask || !this.newTodoListTitle.trim()) return;

    const newTodoList: TodoList = {
      uuid: Date.now().toString(),
      title: this.newTodoListTitle.trim(),
      list: [] as TodoItem[],
    };

    const updatedTask: Task = {
      ...currentTask,
      todo_lists: [...(currentTask.todo_lists || []), newTodoList],
    };

    this.updateTask(updatedTask);
    this.newTodoListTitle = '';
  }

  addTodoItem(todoList: TodoList): void {
    const itemTitle = this.newTodoItemTitles.get(todoList.uuid) || '';
    const currentTask = this.task();
    if (!currentTask || !itemTitle.trim()) return;

    const newTodoItem: TodoItem = {
      uuid: Date.now().toString(),
      title: itemTitle.trim(),
      status: 'pending',
    };

    const updatedTodoLists = currentTask.todo_lists?.map((list) => {
      if (list.uuid === todoList.uuid) {
        return {
          ...list,
          list: [...list.list, newTodoItem],
        };
      }
      return list;
    });

    const updatedTask: Task = {
      ...currentTask,
      todo_lists: updatedTodoLists,
    };

    this.updateTask(updatedTask);
    this.newTodoItemTitles.set(todoList.uuid, '');
  }

  getTodoItemTitle(todoListUuid: string): string {
    return this.newTodoItemTitles.get(todoListUuid) || '';
  }

  setTodoItemTitle(todoListUuid: string, value: string): void {
    this.newTodoItemTitles.set(todoListUuid, value);
  }

  toggleTodoItem(todoList: TodoList, todoItem: TodoItem): void {
    const currentTask = this.task();
    if (!currentTask) return;

    const updatedTodoLists = currentTask.todo_lists?.map((list) => {
      if (list.uuid === todoList.uuid) {
        return {
          ...list,
          list: list.list.map((item) => {
            if (item.uuid === todoItem.uuid) {
              return {
                ...item,
                status: (item.status === 'completed' ? 'pending' : 'completed') as
                  | 'pending'
                  | 'completed',
              };
            }
            return item;
          }),
        };
      }
      return list;
    });

    const updatedTask: Task = {
      ...currentTask,
      todo_lists: updatedTodoLists,
    };

    this.updateTask(updatedTask);
  }

  deleteTodoList(todoList: TodoList): void {
    const currentTask = this.task();
    if (!currentTask) return;

    if (
      confirm(
        this.translocoService.translate('planner.page.confirmDeleteTodoList', {
          name: todoList.title,
        }),
      )
    ) {
      const updatedTodoLists = currentTask.todo_lists?.filter((list) => list.uuid !== todoList.uuid);

      const updatedTask: Task = {
        ...currentTask,
        todo_lists: updatedTodoLists,
      };

      this.updateTask(updatedTask);
    }
  }

  deleteTodoItem(todoList: TodoList, todoItem: TodoItem): void {
    const currentTask = this.task();
    if (!currentTask) return;

    const updatedTodoLists = currentTask.todo_lists?.map((list) => {
      if (list.uuid === todoList.uuid) {
        return {
          ...list,
          list: list.list.filter((item) => item.uuid !== todoItem.uuid),
        };
      }
      return list;
    });

    const updatedTask: Task = {
      ...currentTask,
      todo_lists: updatedTodoLists,
    };

    this.updateTask(updatedTask);
  }

  private updateTask(updatedTask: Task): void {
    const workspaceUuid = this.workspaceUuid();
    const listUuid = this.listUuid();
    if (!workspaceUuid || !listUuid || !updatedTask.uuid) return;

    this.plannerService.updateTask(
      workspaceUuid,
      listUuid,
      updatedTask.uuid,
      updatedTask,
    );

    // Emit the updated task
    this.taskUpdated.emit(updatedTask);
  }

  getTodoProgress(todoList: TodoList): { completed: number; total: number; percentage: number } {
    const total = todoList.list.length;
    const completed = todoList.list.filter((item) => item.status === 'completed').length;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percentage };
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'pending':
        return this.translocoService.translate('planner.status.pending');
      case 'in_progress':
        return this.translocoService.translate('planner.status.inProgress');
      case 'completed':
        return this.translocoService.translate('planner.status.completed');
      case 'cancelled':
        return this.translocoService.translate('planner.status.cancelled');
      default:
        return this.translocoService.translate('planner.status.unknown');
    }
  }

  close(): void {
    this.closed.emit();
  }
}
